import { useAuth } from "@/hooks/useAuth";
import { appSelector } from "@/redux/selectors/app.selector";
import { useSelector } from "@/redux/store";
import { isValidEmail } from "@/util/validator";
import type { ChangeEvent } from "react";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const useLogin = () => {
  const { onRequestEmail } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(
    searchParams.get("authError") ? "Something went wrong signing you in. Please try again." : null,
  );

  const loading = useSelector(appSelector.loading);
  const isValid = isValidEmail(email);

  const onChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setError(null);
    if (searchParams.has("authError")) {
      searchParams.delete("authError");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const onLogin = useCallback(async () => {
    const { success } = await onRequestEmail(email);
    if (!success) {
      setError("We could not find an account for that email address.");
    }
  }, [email, onRequestEmail]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && isValid) onLogin();
    },
    [isValid, onLogin],
  );

  return { email, isValidEmail: isValid, onChange, onKeyDown, onLogin, loading, error };
};
